import { ApiChatMessageRoleChoices } from "@api/graphql/types-and-hooks";

export interface EchoSubheader {
  prefix: string;
  anchor: string;
}

const formatMediaTimeOffset = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
};

/**
 * Resolves the subheader text rendered above a role=COMMENT echo bubble in
 * the expert chat — the "Comment on video @ 4:45" / "Replied to your
 * comment @ 4:45" label. Returns null for any non-echo message.
 *
 * Ported from mobile's expertChatBubbleLogic.ts.
 */
export const resolveEchoSubheader = (args: {
  role: ApiChatMessageRoleChoices;
  isReply: boolean;
  isSentByLoggedInUser: boolean;
  mediaCreatedById: string | null;
  parentAuthorId: string | null;
  loggedInUserId: string | null;
  mediaStartTimeSeconds: number;
}): EchoSubheader | null => {
  const {
    role,
    isReply,
    isSentByLoggedInUser,
    mediaCreatedById,
    parentAuthorId,
    loggedInUserId,
    mediaStartTimeSeconds,
  } = args;

  if (role !== ApiChatMessageRoleChoices.Comment) return null;

  const anchor = `@ ${formatMediaTimeOffset(mediaStartTimeSeconds)}`;

  if (!isReply) {
    if (isSentByLoggedInUser) {
      return { prefix: "You commented on video", anchor };
    }
    if (loggedInUserId && mediaCreatedById && mediaCreatedById === loggedInUserId) {
      return { prefix: "Commented on your video", anchor };
    }
    return { prefix: "Comment on video", anchor };
  }

  // Reply
  if (isSentByLoggedInUser) {
    return { prefix: "You replied on video", anchor };
  }
  if (loggedInUserId && parentAuthorId && parentAuthorId === loggedInUserId) {
    return { prefix: "Replied to your comment", anchor };
  }
  return { prefix: "Reply on video", anchor };
};

/**
 * Parses the commentsCount JSONString field (format:
 * [{"comment_type":"love","count":0}, …]) into a total integer.
 */
export const parseMediaCommentsCount = (raw: string | null | undefined): number | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "number") return parsed;
    if (Array.isArray(parsed)) {
      return parsed.reduce<number>((sum, item) => {
        if (
          typeof item === "object" &&
          item !== null &&
          "count" in item &&
          typeof (item as Record<string, unknown>).count === "number"
        ) {
          return sum + (item as Record<string, number>).count;
        }
        return sum;
      }, 0);
    }
    return null;
  } catch (e) {
    console.error("[chatBubbleLogic] Failed to parse commentsCount:", raw, e);
    return null;
  }
};
